import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, FileText, Copy, Check, Download, Clock, Languages, ChevronDown, ChevronUp, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { LANGUAGES } from '../lib/languagesCatalog';

function getLangName(code) {
  return LANGUAGES.find(l => l.code === code)?.name || code;
}

export default function SessionHistory() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [loadingTranscription, setLoadingTranscription] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Faça login para ver o histórico');
      navigate('/login');
      return;
    }
    // Check if user is admin
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const admin = res.data?.role === 'admin';
        setIsAdmin(admin);
        fetchHistory(token, admin);
      })
      .catch(() => fetchHistory(token, false));
  }, [navigate]);

  const fetchHistory = async (token, admin = false) => {
    try {
      const authToken = token || localStorage.getItem('token');
      const endpoint = admin ? `${API}/admin/sessions/history` : `${API}/sessions/history/all`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSessions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const loadTranscription = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setTranscription(null);
      return;
    }
    setExpandedSession(sessionId);
    setLoadingTranscription(true);
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/transcription`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTranscription(response.data);
    } catch (error) {
      toast.error('Erro ao carregar transcrição');
    } finally {
      setLoadingTranscription(false);
    }
  };

  const copyTranscription = () => {
    if (!transcription?.entries?.length) return;
    const text = transcription.entries
      .map((e, i) => `[${new Date(e.timestamp).toLocaleTimeString('pt-PT')}] ${e.original}`)
      .join('\n');
    const header = `Transcrição - Sessão ${transcription.session_code}\nIdioma: ${getLangName(transcription.source_lang)}\nData: ${new Date(transcription.created_at).toLocaleString('pt-PT')}\n${'─'.repeat(40)}\n\n`;
    navigator.clipboard.writeText(header + text);
    setCopied(true);
    toast.success('Transcrição copiada!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadTranscription = () => {
    if (!transcription?.entries?.length) return;
    const text = transcription.entries
      .map((e) => `[${new Date(e.timestamp).toLocaleTimeString('pt-PT')}] ${e.original}`)
      .join('\n');
    const header = `Transcrição - Sessão ${transcription.session_code}\nIdioma: ${getLangName(transcription.source_lang)}\nData: ${new Date(transcription.created_at).toLocaleString('pt-PT')}\n${'─'.repeat(40)}\n\n`;
    const blob = new Blob([header + text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcricao_${transcription.session_code}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Ficheiro transferido!');
  };

  const downloadPDF = async (sessionId, lang = '') => {
    const langParam = lang ? `?lang=${lang}` : '';
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}/transcription/pdf${langParam}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const langSuffix = lang ? `_${lang}` : '';
      a.download = `transcricao_${sessionId}${langSuffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF transferido!');
    } catch {
      toast.error('Erro ao gerar PDF');
    }
  };

  const statusColors = {
    active: 'bg-green-900/50 text-green-400 border-green-700',
    stopped: 'bg-red-900/50 text-red-400 border-red-700',
    paused: 'bg-amber-900/50 text-amber-400 border-amber-700',
    archived: 'bg-slate-700/50 text-slate-400 border-slate-600',
    created: 'bg-yellow-900/50 text-yellow-400 border-yellow-700',
  };

  const statusLabels = {
    active: 'Ativa',
    stopped: 'Encerrada',
    paused: 'Pausada',
    archived: 'Arquivada',
    created: 'Criada',
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/host')}
            className="text-slate-400 hover:text-white"
            data-testid="back-to-host-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold" data-testid="history-title">
            {isAdmin ? 'Histórico de Todas as Sessões' : 'Histórico de Sessões'}
          </h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : sessions.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="font-['Inter'] text-slate-400">Sem sessões no histórico.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`history-session-${s.code}`}>
                <Card
                  className={`bg-slate-900 border-slate-800 cursor-pointer transition-colors hover:border-slate-700 ${
                    expandedSession === s.id ? 'border-blue-700' : ''
                  }`}
                  onClick={() => loadTranscription(s.id)}
                >
                  <CardContent className="py-4 px-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <span className="font-['JetBrains_Mono'] text-lg font-bold text-blue-400">
                          {s.code}
                        </span>
                        {s.name && <span className="font-['Inter'] text-sm text-slate-300 truncate max-w-[180px]">{s.name}</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusColors[s.status] || statusColors.created}`}>
                          {statusLabels[s.status] || s.status}
                        </span>
                        {isAdmin && s.user_name && (
                          <span className="font-['Inter'] text-xs text-cyan-400 hidden sm:inline">
                            {s.user_name}
                          </span>
                        )}
                        <span className="font-['Inter'] text-xs text-slate-400 hidden sm:inline">
                          <Languages className="w-3 h-3 inline mr-1" />
                          {getLangName(s.source_lang)} → {s.target_langs.map(getLangName).join(', ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {(s.status === 'stopped' || s.status === 'archived') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-cyan-800 text-cyan-400 hover:bg-cyan-950 h-7 px-2 text-xs"
                            onClick={(e) => { e.stopPropagation(); navigate(`/report/${s.id}`); }}
                            data-testid={`history-report-${s.code}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Relatório
                          </Button>
                        )}
                        {s.transcript_count > 0 && (
                          <span className="font-['Inter'] text-xs text-slate-400" data-testid={`transcript-count-${s.code}`}>
                            <FileText className="w-3 h-3 inline mr-1" />
                            {s.transcript_count} entradas
                          </span>
                        )}
                        <span className="font-['Inter'] text-xs text-slate-500">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(s.created_at).toLocaleDateString('pt-PT')}
                        </span>
                        {expandedSession === s.id ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Transcription Panel */}
                {expandedSession === s.id && (
                  <Card className="bg-slate-900/50 border-slate-800 border-t-0 rounded-t-none" data-testid={`transcription-panel-${s.code}`}>
                    <CardContent className="py-4 px-5">
                      {loadingTranscription ? (
                        <div className="flex justify-center py-6">
                          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                      ) : !transcription?.entries?.length ? (
                        <p className="font-['Inter'] text-sm text-slate-500 text-center py-4">
                          Sem transcrição disponível para esta sessão.
                        </p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-['Inter'] text-sm text-slate-400">
                              {transcription.entries.length} frases transcritas
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); copyTranscription(); }}
                                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                data-testid={`copy-transcription-${s.code}`}
                              >
                                {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                                {copied ? 'Copiado' : 'Copiar'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); downloadTranscription(); }}
                                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                data-testid={`download-transcription-${s.code}`}
                              >
                                <Download className="w-3 h-3 mr-1" />
                                TXT
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); downloadPDF(s.id); }}
                                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                data-testid={`download-pdf-${s.code}`}
                              >
                                <FileDown className="w-3 h-3 mr-1" />
                                PDF Original
                              </Button>
                              {s.target_langs?.map(lang => (
                                <Button
                                  key={lang}
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); downloadPDF(s.id, lang); }}
                                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                  data-testid={`download-pdf-${s.code}-${lang}`}
                                >
                                  <FileDown className="w-3 h-3 mr-1" />
                                  PDF {getLangName(lang)}
                                </Button>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-2">
                            {transcription.entries.map((entry, idx) => (
                              <div
                                key={idx}
                                className="flex gap-3 text-sm py-1.5 border-b border-slate-800/50 last:border-0"
                              >
                                <span className="font-['JetBrains_Mono'] text-xs text-slate-500 whitespace-nowrap pt-0.5">
                                  {new Date(entry.timestamp).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </span>
                                <span className="font-['Inter'] text-slate-200">{entry.original}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
